import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { usePermissions } from "@/hooks/usePermissions";
import { storageQueryKeys } from "@/Features/Storage/storage.query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import TaskButton from "@/Monolith-UI/TaskButton.js";

import KeyboardTabOutlinedIcon from "@mui/icons-material/KeyboardTabOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { monolithMoment } from "@/utils/date-format";
import { nanoid } from "nanoid";
import CaseStorageColumnsDefs from "@/components/CaseStorage/CaseStorageColumnsDefs.js";
import OverviewContent from "./OverviewContent";
import CocContent from "../../components/CocContent.js";
import { StoredDataTable } from "@/Features/Acquisitions";
import { EditStorageModal } from "@/Features/Storage/Modals/EditStorageModal";
import {
  convertToEditableStorage,
  resolveStorageRouteParam,
} from "@/Features/Storage/storage.utils";
import { AuditLogsTable } from "@/Features/Audits";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON.js";
import { MONOLITH_PERMISSIONS } from "@/constants.js";
import { useTheme } from "@monolith-forensics/monolith-ui";
import { useFeatures } from "@/contexts/FeaturesContext";
import { StoragePhotosContent } from "@/Features/StoragePhotos/StoragePhotosContent";
import { StorageItem } from "@/Features/Storage/Tables/types";
import { STORAGE_TAB_SLUGS } from "./storageTabSlugs";
import type { StorageTabSlug } from "./storageTabSlugs";

// Local shape for the legacy JS column defs; no Features type exists for them.
interface CaseStorageColumnDef {
  dataField: string;
  caption?: string;
  dataType?: string;
  visible?: boolean;
  render?: (rowData: StorageItem) => ReactNode;
}

const columnDefs = CaseStorageColumnsDefs as CaseStorageColumnDef[];

interface StorageOverviewProps {
  className?: string;
  storageInfo: StorageItem;
}

const StorageOverview = styled(
  ({ className, storageInfo: details }: StorageOverviewProps) => {
    return (
      <div className={className}>
        <div className="inner-content">
          <SideContent details={details} />
          <MainContent details={details} />
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;

  .inner-content {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    gap: 10px;
    width: 100%;
    overflow-y: auto;
  }
`;

interface ContentProps {
  className?: string;
  details: StorageItem;
}

const SideContent = styled(({ className, details }: ContentProps) => {
  const { storage_id } = useParams();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);

  const dataFieldIgnoreList = [
    "description",
    "case_ref",
    "case_number",
    "notes",
  ];

  const storageInfo: Record<string, any> = {
    ...details,
  };

  delete storageInfo.custom_attributes;
  delete storageInfo.case_ref;
  delete storageInfo.case_number;

  const caseInfo: Record<string, any> = {
    case_number: details?.case_number,
    case_ref: details?.case_ref,
  };

  const storageDetails = (
    <div className="detail-container">
      {columnDefs
        .filter((c) => !dataFieldIgnoreList.includes(c.dataField))
        .map((c) => {
          return (
            <div className="detail-item" key={nanoid()}>
              <div className="detail-label">{c.caption}</div>
              {c.render ? (
                c.render(storageInfo)
              ) : c.dataType === "date" ? (
                <>
                  {!!storageInfo[c.dataField] &&
                    monolithMoment({
                      timestamp: storageInfo[c.dataField],
                      includeTime: true,
                    })}
                </>
              ) : (
                <div className="detail-value">{storageInfo[c.dataField]}</div>
              )}
            </div>
          );
        })}
    </div>
  );

  const caseDetails = (
    <div className="detail-container">
      {columnDefs.map((c) => {
        if (!caseInfo[c.dataField]) return null;
        return (
          <div className="detail-item" key={nanoid()}>
            <div className="detail-label">{c.caption}</div>
            {c.render ? (
              c.render(details)
            ) : c.dataType === "date" ? (
              <>
                {monolithMoment({
                  timestamp: caseInfo[c.dataField],
                  includeTime: true,
                })}
              </>
            ) : (
              <div className="detail-value">{caseInfo[c.dataField]}</div>
            )}
          </div>
        );
      })}
    </div>
  );

  const customFieldData = details?.custom_attributes?.filter((c) =>
    c.value === null || c.value === "" || c.value === "[]" ? false : true
  );

  const customDetails = (
    <div className="detail-container">
      {customFieldData?.map((c) => {
        if (c.value === null) return null;
        if (c.value === "") return null;
        if (c.value === "[]") return null;

        const parsedValue = safeParseJSON(c.value);

        return (
          <div className="detail-item" key={nanoid()}>
            <div className="detail-label">{c.field_name}</div>
            {c.editor_type === "dateBox" ? (
              <>
                {monolithMoment({
                  timestamp: c.value as string,
                  includeTime: false,
                })}
              </>
            ) : c.editor_type === "tagBox" ? (
              <div className="detail-value">
                {Array.isArray(parsedValue)
                  ? parsedValue.map((v) => v).join(", ")
                  : parsedValue}
              </div>
            ) : (
              <div className="detail-value">{parsedValue}</div>
            )}
          </div>
        );
      }) || []}
      {customFieldData?.length === 0 && (
        <div className="detail-item">
          <div className="detail-label">No custom data</div>
        </div>
      )}
    </div>
  );

  const onEdit = () => {
    queryClient.refetchQueries({
      queryKey: [
        ...storageQueryKeys.legacyLists,
        resolveStorageRouteParam(storage_id),
      ],
    });
  };

  return (
    <div className={className + (open ? " expanded" : " collapsed")}>
      <div
        className={
          open
            ? "collapse-btn side-content-expanded"
            : "collapse-btn side-content-collapsed"
        }
      >
        {open && <div className="header">Case Info</div>}
        <TaskButton variant="outlined" onClick={() => setOpen(!open)}>
          <KeyboardTabOutlinedIcon style={{ fontSize: 16 }} />
        </TaskButton>
      </div>
      {open && (
        <>
          {caseDetails}
          <div className="header-container">
            <div className="header" style={{ marginRight: 15 }}>
              Storage Item Details
            </div>
            {hasPermission(MONOLITH_PERMISSIONS.STORAGE_UPDATE) && (
              <div
                className="action-menu-item"
                onClick={() => setShowEditModal(true)}
              >
                <EditOutlinedIcon
                  style={{ color: theme.palette.primary.main }}
                />
                <div className="action-menu-label">Edit</div>
              </div>
            )}
          </div>
          {storageDetails}
          <div className="header-container">
            <div className="header" style={{ marginRight: 15 }}>
              Custom Fields
            </div>
          </div>
          {customDetails}
          <EditStorageModal
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onEditSubmit={onEdit}
            defaultStorageInfo={convertToEditableStorage(details)}
          />
        </>
      )}
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  max-width: 425px;
  min-width: 425px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  overflow-y: auto;
  overflow-x: auto;
  transition: all 0.1s ease-in-out;

  &.collapsed {
    min-width: 50px;
    max-width: 50px;
  }

  & .collapse-btn {
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: space-between;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  & .side-content-expanded {
    svg {
      transform: rotate(180deg);
    }
  }

  & .side-content-collapsed {
    justify-content: center;
  }

  & .header {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1.1rem;
    font-weight: 500;
    cursor: default;
  }
  & .header-container {
    display: flex;
    align-items: center;
    align-content: center;
    margin: 20px 0px;
  }
  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-right: 12px;
    margin-left: auto;
    cursor: pointer;
    border-radius: 5px;
    font-size: 12px;
    &:hover {
      text-decoration: underline;
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }

  .detail-container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }

  & .detail-item {
    display: flex;
    align-items: baseline;
    align-content: center;
    margin: 10px 0px;
    font-size: 12px;
  }
  & .detail-label {
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 150px;
  }
  & .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
    width: 100%;
    white-space: pre-wrap;
  }
`;

type StorageTabVisibilityContext = {
  details: StorageItem;
  hasStoragePhotos: boolean;
  hasStorageRead: boolean;
};

type StorageTabDescriptor = {
  slug: StorageTabSlug;
  label: string;
  isVisible?: (ctx: StorageTabVisibilityContext) => boolean;
  /**
   * Once visited, stays mounted (hidden) while another tab is active. For
   * tabs that own work that must outlive a tab switch, such as uploads.
   */
  keepMounted?: boolean;
  render: (details: StorageItem) => ReactNode;
};

const TABS: StorageTabDescriptor[] = [
  {
    slug: STORAGE_TAB_SLUGS.OVERVIEW,
    label: "Overview",
    render: (details) => <OverviewContent storageInfo={details} />,
  },
  {
    slug: STORAGE_TAB_SLUGS.COC,
    label: "Chain of Custody",
    render: (details) => <CocContent storageInfo={details} />,
  },
  {
    slug: STORAGE_TAB_SLUGS.STORED_DATA,
    label: "Stored Data",
    render: (details) => (
      <StoredDataTable
        storageInfo={details as StorageItem & { storage_id: number }}
      />
    ),
  },
  {
    slug: STORAGE_TAB_SLUGS.PHOTOS,
    label: "Photos",
    // Its upload queue lives in the component; unmounting would abort uploads.
    keepMounted: true,
    // Hidden without the flag or read access so opening the page never fires
    // a 403 toast from the photos request.
    isVisible: ({ hasStoragePhotos, hasStorageRead }) =>
      hasStoragePhotos && hasStorageRead,
    render: (details) => (
      <StoragePhotosContent
        storageInfo={
          details as StorageItem & { storage_id: number; uuid: string }
        }
      />
    ),
  },
  {
    slug: STORAGE_TAB_SLUGS.AUDIT,
    label: "Audit Logs",
    render: (details) => (
      <AuditLogsTable
        query={{ object_uuid: details.uuid }}
        stateStoreKey="storage:audit-logs:table"
      />
    ),
  },
];

const MainContent = styled(({ className, details }: ContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();

  const visibilityContext: StorageTabVisibilityContext = {
    details,
    hasStoragePhotos: hasFeatures([FEATURES.STORAGE_PHOTOS]),
    hasStorageRead: hasPermission(MONOLITH_PERMISSIONS.STORAGE_READ_ALL),
  };

  const visibleTabs = TABS.filter(
    (tab) => !tab.isVisible || tab.isVisible(visibilityContext)
  );

  const requestedSlug = searchParams.get("tab") ?? "";
  const activeTab =
    visibleTabs.find((tab) => tab.slug === requestedSlug) ?? visibleTabs[0];
  const activeSlug = activeTab.slug;

  // keepMounted tabs that have been opened for this storage item.
  const [visited, setVisited] = useState<{ uuid?: string; slugs: string[] }>({
    slugs: [],
  });
  const visitedSlugs = useMemo(
    () => (visited.uuid === details.uuid ? visited.slugs : []),
    [visited, details.uuid]
  );
  const mountedSlugs = useMemo(
    () =>
      activeTab.keepMounted && !visitedSlugs.includes(activeSlug)
        ? [...visitedSlugs, activeSlug]
        : visitedSlugs,
    [activeTab.keepMounted, activeSlug, visitedSlugs]
  );

  useEffect(() => {
    if (
      mountedSlugs.length !== visitedSlugs.length ||
      visited.uuid !== details.uuid
    ) {
      setVisited({ uuid: details.uuid, slugs: mountedSlugs });
    }
  }, [mountedSlugs, visitedSlugs.length, visited.uuid, details.uuid]);

  useEffect(() => {
    if (requestedSlug && requestedSlug !== activeSlug) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [requestedSlug, activeSlug, searchParams, setSearchParams]);

  const buttonStyle = {
    minWidth: "150px",
    padding: "2px 5px",
  };

  const handleChange = (_event: unknown, nextSlug: StorageTabSlug | null) => {
    if (nextSlug === null) return;
    if (!visibleTabs.some((tab) => tab.slug === nextSlug)) return;
    const next = new URLSearchParams(searchParams);
    if (nextSlug === STORAGE_TAB_SLUGS.OVERVIEW) {
      next.delete("tab");
    } else {
      next.set("tab", nextSlug);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className={className}>
      <div className="toggle-menu">
        <ToggleButtonGroup
          size="small"
          value={activeSlug}
          onChange={handleChange}
          exclusive
          style={{ margin: "auto" }}
        >
          {visibleTabs.map((tab) => (
            <ToggleButton
              key={tab.slug}
              value={tab.slug}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      {!activeTab.keepMounted && activeTab.render(details)}
      {visibleTabs
        .filter((tab) => tab.keepMounted && mountedSlugs.includes(tab.slug))
        .map((tab) => (
          <div
            key={`${tab.slug}:${details.uuid}`}
            style={{
              display: tab.slug === activeSlug ? "flex" : "none",
              flexDirection: "column",
              flex: "1 1 auto",
              minHeight: 0,
            }}
          >
            {tab.render(details)}
          </div>
        ))}
    </div>
  );
})`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0px;

  & .toggle-menu {
    margin-bottom: 10px;
    display: flex;
    overflow-x: auto;
    min-height: 30px;
  }
  & .header {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1.1rem;
    font-weight: 500;
  }
  & .description {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
    margin-top: 10px;
  }
`;

export default StorageOverview;
